import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import {
  Card,
  Table,
  Button,
  Tag,
  Radio,
  Select,
  Row,
  Col,
  Typography,
  Space,
  Spin,
  Alert,
} from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import { reducer, sliceKey, actions } from "./slice";
import { parameterComplianceSaga } from "./saga";
import * as selectors from "./selectors";

const { Title } = Typography;
const { Option } = Select;

// Compliance color mapping
const complianceTag = (value) => {
  let color = "green";
  if (value < 95) color = "orange";
  if (value < 90) color = "red";
  return <Tag color={color}>{value ? value.toFixed(1) : 0}%</Tag>;
};

const ComplianceSummary = ({ filters = {}, onDetailedClick = () => {} }) => {
  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({ key: sliceKey, saga: parameterComplianceSaga });
  const dispatch = useDispatch();
  const loading = useSelector(selectors.selectLoading);
  const parameters = useSelector(selectors.selectParameters);
  const filtersLoading = useSelector(selectors.selectFiltersLoading);
  const testTypeOptions = useSelector(selectors.selectTestTypeOptions);

  const [view, setView] = useState("top");
  const [dimension, setDimension] = useState("All");
  const [testType, setTestType] = useState("All");

  const handleViewChange = (newView) => setView(newView);
  const handleDimensionChange = (e) => setDimension(e.target.value);
  const handleTestTypeChange = (value) => setTestType(value);

  // Fetch filters only once on mount
  useEffect(() => {
    dispatch(actions.fetchFilters());
  }, [dispatch]);
  
  // Memoize the filters object to prevent unnecessary re-renders
  const memoizedFilters = useMemo(() => {
    return {
      ...filters,
      viewType: view,
      parameterType: dimension,
      testType: testType !== "All" ? testType : undefined,
    };
  }, [filters, view, dimension, testType]);

  // Memoize the filters string
  const filtersString = useMemo(() => {
    return JSON.stringify(memoizedFilters);
  }, [memoizedFilters]);

  // Dispatch only when filtersString actually changes
  useEffect(() => {
    dispatch(actions.fetchCompliance({ filtersString }));
  }, [dispatch, filtersString]); // ← Only depends on filtersString

  const getProcessedData = () => {
    if (!parameters || parameters.length === 0) return [];
    let processedData = parameters.map((param) => {
      const currData = {
        key: param.parameter,
        testType: param.testType,
        parameter: param.parameter,
        totalOrders: param.total,
        unsafe: param.unsafe,
        safe: param.safe,
        standard: param.standard,
        subStandard: param.subStandard,
        compliance: param.overallCompliance,
        safetyCompliance: param.safetyCompliance,
        qualityCompliance: param.qualityCompliance,
        unit: param.unit,
      };
      let totalChecks =
        param.safe + param.unsafe + param.standard + param.subStandard;
      if (dimension === "Safety") {
        totalChecks = param.safe + param.unsafe;
      }
      if (dimension === "Quality") {
        totalChecks = param.standard + param.subStandard;
      }
      currData.totalChecks = totalChecks;
      return currData;
    });
    processedData.sort((a, b) => {
      if (view === "top") {
        return b.compliance - a.compliance;
      } else {
        return a.compliance - b.compliance;
      }
    });

    return processedData.slice(0, 10);
  };

  const getColumns = () => {
    const baseColumns = [
      {
        title: "Test Type",
        dataIndex: "testType",
        width: 120,
        ellipsis: true,
      },
      {
        title: "Parameter",
        dataIndex: "parameter",
        width: 250,
        ellipsis: true,
      },
      {
        title: "Total Orders",
        dataIndex: "totalOrders",
        width: 120,
        align: "center",
      },
      {
        title: "Total Checks",
        dataIndex: "totalChecks",
        width: 120,
        align: "center",
      },
    ];

    switch (dimension) {
      case "Safety":
        return [
          ...baseColumns,
          { title: "Unsafe", dataIndex: "unsafe", width: 80, align: "center" },
          { title: "Safe", dataIndex: "safe", width: 80, align: "center" },
          {
            title: "Safety Compliance %",
            dataIndex: "safetyCompliance",
            width: 150,
            align: "center",
            render: complianceTag,
          },
        ];
      case "Quality":
        return [
          ...baseColumns,
          {
            title: "Standard",
            dataIndex: "standard",
            width: 100,
            align: "center",
          },
          {
            title: "Sub-Standard",
            dataIndex: "subStandard",
            width: 120,
            align: "center",
          },
          {
            title: "Quality Compliance %",
            dataIndex: "qualityCompliance",
            width: 150,
            align: "center",
            render: complianceTag,
          },
        ];
      case "All":
        return [
          ...baseColumns,
          { title: "Unsafe", dataIndex: "unsafe", width: 80, align: "center" },
          { title: "Safe", dataIndex: "safe", width: 80, align: "center" },
          {
            title: "Standard",
            dataIndex: "standard",
            width: 100,
            align: "center",
          },
          {
            title: "Sub-Standard",
            dataIndex: "subStandard",
            width: 120,
            align: "center",
          },
          {
            title: "Overall Compliance %",
            dataIndex: "compliance",
            width: 150,
            align: "center",
            render: complianceTag,
          },
        ];
      default:
        return baseColumns;
    }
  };

  const processedData = getProcessedData();

  return (
    <Card bordered={false}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            Parameter-wise Compliance
          </Title>
        </Col>
        <Col>
          <Button
            onClick={onDetailedClick}
            icon={<ExportOutlined style={{ color: "#fff" }} />}
            style={{
              backgroundColor: "#1e2a38",
              border: "1px solid #fff",
              color: "#fff",
            }}
          >
            Detailed View
          </Button>
        </Col>
      </Row>

      {/* Controls Row */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button
              type={view === "top" ? "primary" : "default"}
              icon={<ArrowUpOutlined />}
              onClick={() => handleViewChange("top")}
              loading={loading}
            >
              Top 10
            </Button>
            <Button
              type={view === "bottom" ? "primary" : "default"}
              icon={<ArrowDownOutlined />}
              onClick={() => handleViewChange("bottom")}
              loading={loading}
            >
              Bottom 10
            </Button>
          </Space>
        </Col>
        <Col>
          <Select
            value={testType}
            onChange={handleTestTypeChange}
            style={{ width: 180 }}
            loading={filtersLoading}
            placeholder="Select test type"
            notFoundContent={filtersLoading ? <Spin size="small" /> : "No data"}
          >
            <Option value="All">All Test Types</Option>
            {testTypeOptions.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>

      <Radio.Group
        value={dimension}
        onChange={handleDimensionChange}
        buttonStyle="solid"
        style={{ display: "flex", width: "100%", marginBottom: 16 }}
        disabled={loading}
      >
        <Radio.Button value="All" style={{ flex: 1, textAlign: "center" }}>
          Overall
        </Radio.Button>
        <Radio.Button value="Safety" style={{ flex: 1, textAlign: "center" }}>
          Safety
        </Radio.Button>
        <Radio.Button value="Quality" style={{ flex: 1, textAlign: "center" }}>
          Quality
        </Radio.Button>
      </Radio.Group>

      <div style={{ position: "relative" }}>
        {loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1,
            }}
          >
            <Spin size="large" />
          </div>
        )}
        <Table
          bordered
          dataSource={processedData}
          columns={getColumns()}
          pagination={false}
          scroll={{ x: 1000 }}
          size="small"
          locale={{
            emptyText: loading ? " " : "No data available",
          }}
        />
      </div>
    </Card>
  );
};

export default ComplianceSummary;